import React, { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Col, Empty, Row, Select, Skeleton,
  Space, Tag, Typography, message,
} from 'antd';
import { ArrowUpOutlined } from '@ant-design/icons';
import * as api from '../api/client';
import type { FeatureRequestItem, FeatureRequestStatus } from '../api/client';

const { Title, Text } = Typography;

// ── Config ────────────────────────────────────────────────────────────────────

const COLUMNS: Array<{ title: string; status: FeatureRequestStatus; color: string; badgeColor: string }> = [
  { title: 'Under Consideration', status: 'UNDER_CONSIDERATION', color: '#722ed1', badgeColor: 'purple' },
  { title: 'Planned',             status: 'PLANNED',             color: '#13c2c2', badgeColor: 'cyan'   },
  { title: 'In Development',      status: 'IN_DEVELOPMENT',      color: '#faad14', badgeColor: 'gold'   },
  { title: 'Released',            status: 'RELEASED',            color: '#52c41a', badgeColor: 'green'  },
];

const STATUS_OPTIONS: Array<{ value: FeatureRequestStatus; label: string }> = [
  { value: 'UNDER_CONSIDERATION', label: 'Under Consideration' },
  { value: 'PLANNED',             label: 'Planned'             },
  { value: 'IN_DEVELOPMENT',      label: 'In Development'      },
  { value: 'RELEASED',            label: 'Released'            },
];

const CATEGORY_LABELS: Record<string, string> = {
  AI_AGENTS: 'AI Agents', PLAYBOOKS: 'Playbooks', KNOWLEDGE_BASE: 'Knowledge Base',
  API: 'API', INTEGRATIONS: 'Integrations', DESKTOP_APP: 'Desktop App',
  UI_UX: 'UI / UX', ADMIN: 'Admin', SECURITY: 'Security', BILLING: 'Billing', OTHER: 'Other',
};

const formatDate = (d?: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return '—'; }
};

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  isAdmin?: boolean;
  adminEmail?: string;
  onOpenDetails: (id: string) => void;
}

const FeatureRoadmap: React.FC<Props> = ({ isAdmin = false, adminEmail = '', onOpenDetails }) => {
  const [items, setItems] = useState<FeatureRequestItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getFeatureRequests({ sort: 'recentlyUpdated' });
      setItems(res.data.items || []);
    } catch {
      message.error('Failed to load roadmap.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const moveStatus = async (id: string, status: FeatureRequestStatus) => {
    try {
      await api.updateFeatureRequestStatus(id, { status }, adminEmail);
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Status update failed.');
    }
  };

  const counts = Object.fromEntries(
    COLUMNS.map(col => [col.status, items.filter(i => i.status === col.status).length]),
  );

  return (
    <div>
      {/* Summary stats */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {COLUMNS.map(col => (
          <Col key={col.status} xs={12} sm={6}>
            <Card bodyStyle={{ padding: '12px 16px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{col.title}</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: col.color, lineHeight: 1.2 }}>
                {loading ? '—' : counts[col.status]}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Kanban columns */}
      {loading ? (
        <Row gutter={[12, 12]}>
          {COLUMNS.map(col => (
            <Col key={col.status} xs={24} sm={12} lg={6}>
              <Card
                title={col.title}
                headStyle={{ borderTop: `3px solid ${col.color}`, paddingTop: 8 }}
              >
                <Skeleton active />
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Row gutter={[12, 12]}>
          {COLUMNS.map(col => {
            const cards = items.filter(i => i.status === col.status);
            return (
              <Col key={col.status} xs={24} sm={12} lg={6}>
                <Card
                  className="praxys-roadmap-column"
                  title={
                    <Space>
                      <Text strong>{col.title}</Text>
                      <Badge count={cards.length} style={{ backgroundColor: col.color }} />
                    </Space>
                  }
                  headStyle={{ borderTop: `3px solid ${col.color}`, paddingTop: 8 }}
                  bodyStyle={{ padding: '8px 12px' }}
                >
                  {cards.length === 0 ? (
                    <Empty
                      description="No items"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      style={{ margin: '16px 0' }}
                    />
                  ) : (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {cards.map(card => (
                        <Card
                          key={card.id}
                          className="praxys-roadmap-card"
                          size="small"
                          bodyStyle={{ padding: 10 }}
                          hoverable
                        >
                          <Text
                            strong
                            style={{ fontSize: 13, cursor: 'pointer', display: 'block', marginBottom: 6 }}
                            onClick={() => onOpenDetails(card.id)}
                          >
                            {card.title}
                          </Text>
                          <Space size={6} wrap style={{ marginBottom: 6 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <ArrowUpOutlined /> {card.voteCount}
                            </Text>
                            <Tag style={{ margin: 0, fontSize: 11 }}>
                              {CATEGORY_LABELS[card.category] || card.category}
                            </Tag>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {formatDate(card.updatedOn || card.createdOn)}
                            </Text>
                          </Space>
                          <Space size={6}>
                            <Button size="small" onClick={() => onOpenDetails(card.id)}>
                              View
                            </Button>
                            {isAdmin && (
                              <Select
                                size="small"
                                value={card.status}
                                style={{ width: 150 }}
                                onChange={(v: FeatureRequestStatus) => moveStatus(card.id, v)}
                                options={STATUS_OPTIONS}
                              />
                            )}
                          </Space>
                        </Card>
                      ))}
                    </Space>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default FeatureRoadmap;
